// ---------------------------------------------------------------------------
// Ort der Szene aus dem Text (PLAN.md §16a): Der erste im Draft verknüpfte Ort wird Schauplatz der Szene (Feld `place`), solange der
// Autor ihn nicht selbst anders gewählt hat. Wechselt der verknüpfte Ort im Text, ist das ein möglicher Ortswechsel – der Editor
// schlägt ihn vor. Was in Rede oder Gedanken steht („Er dachte an Paris“), zählt nicht.
// ---------------------------------------------------------------------------

import type { StoryCtx } from './ctx.js';
import { draftOf } from './narrative.js';
import { mentionsIn, refIndex } from './refs.js';
import { spansIn } from './spans.js';
import type { IgnoredTerm, StoryNode } from './types.js';

/** Ist der Eintrag ein Ort (Klasse der Kategorie Ort, auch eigene)? */
export const isPlaceEntity = (ctx: StoryCtx, entityId: string): boolean => {
  const e = ctx.entities.get(entityId);
  return !!e && ctx.types.get(e.typeId)?.category === 'place';
};

/** Schauplatz einer Szene (Eintrag), sofern gesetzt und vorhanden. */
export const placeOf = (ctx: StoryCtx, scene: StoryNode): string | null => {
  const v = scene.fields.place;
  return typeof v === 'string' && ctx.entities.has(v) ? v : null;
};

export interface PlaceLink {
  entityId: string;
  key: string;
  /** Lage des „@schlüssel“ im Text */
  index: number;
  length: number;
}

/** Bezüge, in denen ein Ort nicht der Schauplatz ist: was jemand sagt oder denkt */
const ELSEWHERE = new Set(['rede', 'gedanke']);

/** Verknüpfte Orte in einem Text (Markdown), in Textreihenfolge – ohne die in Rede und Gedanken. */
export function placeLinksIn(ctx: StoryCtx, text: string): PlaceLink[] {
  if (!text.includes('@')) return [];
  const refs = refIndex(ctx);
  const quoted = spansIn(text).filter((s) => s.role && ELSEWHERE.has(s.role));
  const out: PlaceLink[] = [];
  for (const m of mentionsIn(text)) {
    const e = refs.get(m.key);
    if (!e || !isPlaceEntity(ctx, e.id)) continue;
    if (quoted.some((s) => s.from <= m.index && m.index < s.to)) continue;
    out.push({ entityId: e.id, key: m.key, index: m.index, length: m.length });
  }
  return out;
}

/** Der Ort, den ein Text als Schauplatz nennt: der erste verknüpfte (außerhalb von Rede und Gedanken); null ohne. */
export const textPlaceOf = (ctx: StoryCtx, text: string): string | null => placeLinksIn(ctx, text)[0]?.entityId ?? null;

/** Ein möglicher Ortswechsel: die Stelle, an der der verknüpfte Ort von `fromId` zu einem anderen wechselt. */
export type PlaceShift<T extends { entityId: string }> = T & { fromId: string };

/** Ortswechsel in einer Folge verknüpfter Orte: jede Stelle, deren Ort ein anderer ist als der davor. */
export function placeShifts<T extends { entityId: string }>(links: readonly T[]): PlaceShift<T>[] {
  const out: PlaceShift<T>[] = [];
  for (let i = 1; i < links.length; i++) {
    const prev = links[i - 1]!.entityId;
    if (links[i]!.entityId !== prev) out.push({ ...links[i]!, fromId: prev });
  }
  return out;
}

/**
 * Schauplatz nach einer Änderung des Drafts: der Ort aus dem Text, wenn er sich geändert hat und der Schauplatz leer ist oder noch
 * dem Text folgte (also dem bisherigen Ort aus dem Text entspricht); `undefined`: nichts zu ändern. Einen selbst gewählten Schauplatz
 * überschreibt der Text nie; verschwindet der Ort aus dem Text, bleibt der Schauplatz (wie die Zeit, PLAN.md §16).
 */
export function placeFromText(ctx: StoryCtx, before: StoryNode, after: StoryNode): string | undefined {
  const was = textPlaceOf(ctx, draftOf(before));
  const now = textPlaceOf(ctx, draftOf(after));
  if (!now || now === was) return undefined;
  const current = placeOf(ctx, after);
  if (current !== null && current !== was) return undefined;
  return now === current ? undefined : now;
}

// ---------------------------------------------------------------------------
// „Kein Ortswechsel“ merken: als Ausnahme des Dokuments (`TextDocument.ignored`) in der Form `{term: '@nach', before: '@von'}` –
// ein Vorkommen beginnt nie mit „@“. Gilt für jeden Wechsel von diesem zu jenem Ort im Dokument.
// ---------------------------------------------------------------------------

export const shiftIgnored = (fromKey: string, toKey: string): IgnoredTerm => ({ term: `@${toKey}`, before: `@${fromKey}`, after: '' });

export const isShiftIgnored = (x: IgnoredTerm): boolean => x.term.startsWith('@') && x.before.startsWith('@');

/** Ist der Wechsel `fromKey` → `toKey` als „kein Ortswechsel“ gemerkt? */
export const isShiftIgnoredIn = (ignored: readonly IgnoredTerm[] | undefined, fromKey: string, toKey: string): boolean =>
  !!ignored?.some((x) => x.term === `@${toKey}` && x.before === `@${fromKey}`);

